<template>
  <div class="flex flex-col h-full bg-slate-50 dark:bg-slate-950">
    <!-- Page Header -->
    <div class="flex items-center justify-between px-6 py-4 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
      <div class="flex items-center gap-3">
        <span class="i-lucide-kanban size-5 text-woot-500" />
        <div>
          <h1 class="text-base font-bold text-slate-800 dark:text-slate-100">CRM</h1>
          <p class="text-xs text-slate-500">Gerencie seus negócios e oportunidades</p>
        </div>
      </div>

      <div class="flex items-center gap-2">
        <!-- Pipeline management -->
        <div class="relative" ref="pipelineMenuRef">
          <button
            class="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 transition-colors"
            @click="showPipelineMenu = !showPipelineMenu"
          >
            <span class="i-lucide-settings size-3.5" />
            Pipelines
            <span class="i-lucide-chevron-down size-3" />
          </button>

          <!-- Pipeline dropdown -->
          <div
            v-if="showPipelineMenu"
            class="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 z-10 overflow-hidden"
          >
            <div class="py-1">
              <div
                v-for="pipeline in pipelines"
                :key="pipeline.id"
                class="flex items-center justify-between px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer group"
                @click="selectPipeline(pipeline)"
              >
                <div class="flex items-center gap-2">
                  <span
                    class="size-2 rounded-full"
                    :class="currentPipeline?.id === pipeline.id ? 'bg-woot-500' : 'bg-slate-300'"
                  />
                  <span class="text-sm text-slate-700 dark:text-slate-200">{{ pipeline.name }}</span>
                </div>
                <button
                  class="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-400"
                  @click.stop="editPipeline(pipeline)"
                >
                  <span class="i-lucide-pencil size-3" />
                </button>
              </div>
              <div class="border-t border-slate-200 dark:border-slate-700 mt-1 pt-1">
                <button
                  class="w-full flex items-center gap-2 px-3 py-2 text-sm text-woot-600 hover:bg-woot-50 dark:hover:bg-woot-900/20 transition-colors"
                  @click="openCreatePipeline"
                >
                  <span class="i-lucide-plus size-3.5" />
                  Novo pipeline
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Kanban Board -->
    <div class="flex-1 overflow-hidden">
      <KanbanBoard
        :agents="agents"
        @add-card="openCreateCard"
        @add-stage="openCreateStage"
        @edit-stage="openEditStage"
        @card-click="openCardDetail"
        @edit-card="openEditCard"
        @delete-card="handleDeleteCard"
        @create-pipeline="openCreatePipeline"
      />
    </div>

    <!-- Modals -->
    <CardFormModal
      v-if="showCardModal"
      :card="editingCard"
      :stages="currentStages"
      :agents="agents"
      :default-stage-id="defaultStageId"
      :is-saving="isSavingCard"
      @close="closeCardModal"
      @submit="handleCardSubmit"
    />

    <CardDetailModal
      v-if="showCardDetail && selectedCard"
      :card="selectedCard"
      :stages="currentStages"
      @close="showCardDetail = false"
      @edit="openEditCard"
    />

    <PipelineFormModal
      v-if="showPipelineModal"
      :pipeline="editingPipeline"
      :is-saving="isSavingPipeline"
      @close="closePipelineModal"
      @submit="handlePipelineSubmit"
    />

    <StageFormModal
      v-if="showStageModal"
      :stage="editingStage"
      :is-saving="isSavingStage"
      @close="closeStageModal"
      @submit="handleStageSubmit"
      @delete="handleDeleteStage"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import KanbanBoard from './components/KanbanBoard.vue';
import CardFormModal from './components/CardFormModal.vue';
import CardDetailModal from './components/CardDetailModal.vue';
import PipelineFormModal from './components/PipelineFormModal.vue';
import StageFormModal from './components/StageFormModal.vue';
import CrmAPI from '../../../api/crm';

const store = useStore();

// State
const showCardModal = ref(false);
const showCardDetail = ref(false);
const showPipelineModal = ref(false);
const showStageModal = ref(false);
const showPipelineMenu = ref(false);
const editingCard = ref(null);
const editingPipeline = ref(null);
const editingStage = ref(null);
const selectedCard = ref(null);
const defaultStageId = ref(null);
const isSavingCard = ref(false);
const isSavingPipeline = ref(false);
const isSavingStage = ref(false);
const pipelineMenuRef = ref(null);

// Computed
const pipelines = computed(() => store.getters['crm/getPipelines']);
const currentPipeline = computed(() => store.getters['crm/getCurrentPipeline']);
const currentStages = computed(() => {
  if (!currentPipeline.value) return [];
  return (currentPipeline.value.stages || currentPipeline.value.crm_stages || []).sort((a, b) => a.position - b.position);
});
const agents = computed(() => store.getters['agents/getAgents'] || []);

// Card modal
function openCreateCard(stage) {
  editingCard.value = null;
  defaultStageId.value = stage?.id || currentStages.value[0]?.id || null;
  showCardModal.value = true;
}

function openEditCard(card) {
  editingCard.value = card;
  showCardDetail.value = false;
  showCardModal.value = true;
}

function closeCardModal() {
  showCardModal.value = false;
  editingCard.value = null;
}

async function handleCardSubmit(formData) {
  if (!currentPipeline.value) return;
  isSavingCard.value = true;
  try {
    if (editingCard.value?.id) {
      await store.dispatch('crm/updateCard', {
        pipelineId: currentPipeline.value.id,
        cardId: editingCard.value.id,
        cardData: formData,
      });
    } else {
      await store.dispatch('crm/createCard', {
        pipelineId: currentPipeline.value.id,
        cardData: formData,
      });
    }
    closeCardModal();
  } finally {
    isSavingCard.value = false;
  }
}

async function handleDeleteCard(card) {
  if (!currentPipeline.value) return;
  if (!confirm(`Excluir o card "${card.title}"?`)) return;
  await store.dispatch('crm/deleteCard', {
    pipelineId: currentPipeline.value.id,
    cardId: card.id,
    stageId: card.stage_id,
  });
}

// Card detail
function openCardDetail(card) {
  selectedCard.value = card;
  showCardDetail.value = true;
}

// Pipeline modal
function openCreatePipeline() {
  editingPipeline.value = null;
  showPipelineMenu.value = false;
  showPipelineModal.value = true;
}

function editPipeline(pipeline) {
  editingPipeline.value = pipeline;
  showPipelineMenu.value = false;
  showPipelineModal.value = true;
}

function closePipelineModal() {
  showPipelineModal.value = false;
  editingPipeline.value = null;
}

async function handlePipelineSubmit(formData) {
  isSavingPipeline.value = true;
  try {
    if (editingPipeline.value?.id) {
      await store.dispatch('crm/updatePipeline', { id: editingPipeline.value.id, ...formData });
    } else {
      await store.dispatch('crm/createPipeline', formData);
    }
    closePipelineModal();
  } finally {
    isSavingPipeline.value = false;
  }
}

function selectPipeline(pipeline) {
  store.dispatch('crm/setCurrentPipeline', pipeline);
  showPipelineMenu.value = false;
}

// Stage modal
function openCreateStage(pipeline) {
  editingStage.value = null;
  showStageModal.value = true;
}

function openEditStage(stage) {
  editingStage.value = stage;
  showStageModal.value = true;
}

function closeStageModal() {
  showStageModal.value = false;
  editingStage.value = null;
}

async function handleStageSubmit(formData) {
  if (!currentPipeline.value) return;
  isSavingStage.value = true;
  try {
    if (editingStage.value?.id) {
      await CrmAPI.updateStage(currentPipeline.value.id, editingStage.value.id, { stage: formData });
    } else {
      await CrmAPI.createStage(currentPipeline.value.id, { stage: formData });
    }
    // Refresh pipeline to get updated stages
    await store.dispatch('crm/fetchPipelines');
    closeStageModal();
  } finally {
    isSavingStage.value = false;
  }
}

async function handleDeleteStage(stage) {
  if (!currentPipeline.value) return;
  if (!confirm(`Excluir a coluna "${stage.name}"? Os cards serão perdidos.`)) return;
  await CrmAPI.deleteStage(currentPipeline.value.id, stage.id);
  await store.dispatch('crm/fetchPipelines');
  closeStageModal();
}

// Close pipeline menu on outside click
function handleOutsideClick(e) {
  if (pipelineMenuRef.value && !pipelineMenuRef.value.contains(e.target)) {
    showPipelineMenu.value = false;
  }
}

onMounted(() => {
  document.addEventListener('click', handleOutsideClick);
  store.dispatch('agents/get');
});

onUnmounted(() => {
  document.removeEventListener('click', handleOutsideClick);
});
</script>
